<template>
  <div id="app">
    <NavBar />
    <main class="main-content">
      <OrgLifecycleBanner />
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>
    <AppFooter />
    <AuthManager />
    <Notification v-if="notification.show" :notification="notification" />
    <PendingItemsModal
      v-if="showPendingModal"
      :initial-items="pendingItems"
      @close="onPendingModalClose"
      @accepted="onInvitationAccepted"
    />
  </div>
</template>

<script>
import NavBar from '@/components/NavBar.vue'
import AppFooter from '@/components/AppFooter.vue'
import AuthManager from '@/components/auth/AuthManager.vue'
import Notification from '@/components/Notification.vue'
import PendingItemsModal from '@/components/auth/PendingItemsModal.vue'
import OrgLifecycleBanner from '@/components/OrgLifecycleBanner.vue'
import { lockedRedirectFor } from '@/utils/org-lifecycle'
import { authService } from '@/services/auth-api'
import { mapState, mapMutations } from 'vuex'

// Phase G v1.2 — once a user has seen (or dismissed) the pending-invitations
// modal in this session, don't keep popping it up on every page change.
const PENDING_MODAL_FLAG = 'cs:pending-items-shown'

export default {
  name: 'App',
  components: {
    NavBar,
    AppFooter,
    AuthManager,
    Notification,
    PendingItemsModal,
    OrgLifecycleBanner
  },
  data() {
    return {
      showPendingModal: false,
      pendingItems: []
    }
  },
  computed: {
    ...mapState(['notification']),
    isAuthenticated() {
      return this.$store.state.auth?.isAuthenticated
    },
    activeOrgLifecycleKey() {
      const u = this.$store.getters['auth/currentUser']
      const org = (u?.organizations || []).find((o) => o && o.is_current) || u?.current_organization
      return org ? `${org.id}:${org.lifecycle_state || 'active'}` : ''
    }
  },
  watch: {
    // Tenant lifecycle: the router guard decides on navigation, but /me can
    // report a lock (or an unlock after paying) while the user sits on a
    // page. Re-run the same decision whenever the active org's state moves.
    activeOrgLifecycleKey() {
      if (!this.isAuthenticated) return
      const target = lockedRedirectFor(this.$route?.path, this.$store.getters['auth/currentUser'])
      if (target && target !== this.$route?.path) {
        this.$router.replace(target).catch(() => {})
      }
    },
    // When auth flips to true (initial load with persisted session, or fresh
    // login), check for pending invitations once per session.
    isAuthenticated: {
      immediate: true,
      handler(authed) {
        if (authed) {
          this.maybeShowPendingItems()
        } else {
          // Logout resets the session flag so the next user sees the modal.
          try { sessionStorage.removeItem(PENDING_MODAL_FLAG) } catch {}
          this.showPendingModal = false
          this.pendingItems = []
          // If we just landed back on /?invitation_token=..., re-prompt.
          this.maybeOpenAuthForInvitation()
        }
      }
    },
    // Phase G v1.2 — when the user clicks an "Accept invitation" email link
    // while logged out, InvitationAccept.vue redirects to
    // `/?invitation_token=<token>`. Surface the auth modal automatically.
    '$route.query.invitation_token': {
      immediate: true,
      handler() {
        this.maybeOpenAuthForInvitation()
      }
    }
  },
  methods: {
    ...mapMutations(['setShowAuthModal', 'setAuthModalType', 'setInvitationContext']),

    async maybeOpenAuthForInvitation() {
      const token = this.$route?.query?.invitation_token
      if (!token) return
      if (this.isAuthenticated) return
      if (this.$store.state.showAuthModal) return

      // Persist the token so it survives the registration → activation
      // round trip; PendingItemsModal / Members view picks it back up.
      try { localStorage.setItem('cs:invitation-token', token) } catch {}

      // Resolve the token to its target email so we can route a
      // returning user to the login modal instead of dragging them
      // through registration. Backend returns user_exists per the
      // invitee's email; on lookup failure we fall back to register
      // (the historical default).
      let lookup = null
      try {
        lookup = await authService.lookupInvitation(token)
      } catch {
        lookup = null
      }

      // Stash context so the modals can show a contextual banner +
      // prefill the email field.
      this.setInvitationContext(
        lookup
          ? {
              token,
              email: lookup.email,
              org_name: lookup.org_name,
              role: lookup.role,
              status: lookup.status,
              user_exists: !!lookup.user_exists,
              expires_at: lookup.expires_at,
            }
          : { token }
      )

      // Route based on whether the invited email is an existing user.
      // Existing users: skip the signup form, send them straight to
      // login with the email pre-filled. New users: register flow.
      const wantsLogin = !!lookup?.user_exists
      this.setAuthModalType(wantsLogin ? 'login' : 'register')
      this.setShowAuthModal(true)
    },

    async maybeShowPendingItems() {
      // Don't probe on /activate or /reset-password — the user is mid-flow.
      const path = this.$route?.path || ''
      if (path.startsWith('/activate') || path.startsWith('/reset-password')) return

      let alreadyShown = false
      try {
        alreadyShown = sessionStorage.getItem(PENDING_MODAL_FLAG) === '1'
      } catch {}
      if (alreadyShown) return

      try {
        const items = await authService.getPendingInvitations()
        if (Array.isArray(items) && items.length > 0) {
          this.pendingItems = items
          this.showPendingModal = true
          // Mark immediately so a route change doesn't re-fire the call;
          // the user can still close + revisit via /admin/members.
          try { sessionStorage.setItem(PENDING_MODAL_FLAG, '1') } catch {}
        }
      } catch (err) {
        // Silently swallow — this is a polite surface, not a hard failure.
        console.warn('Pending-invitations probe failed:', err?.message || err)
      }
    },

    onPendingModalClose() {
      this.showPendingModal = false
    },

    onInvitationAccepted() {
      // Future hook: refresh /me to pick up the new membership in NavBar /
      // org-switcher. For v1.2 we leave the call site responsible.
    },

    preventDevTools() {
      // Disable F12, Ctrl+Shift+I, Ctrl+U for unauthorized users
      document.addEventListener('keydown', (e) => {
        if (!this.$store.state.auth.isAuthenticated) {
          if (e.key === 'F12' ||
              (e.ctrlKey && e.shiftKey && e.key === 'I') ||
              (e.ctrlKey && e.key === 'u')) {
            e.preventDefault()
            this.$store.dispatch('showNotification', {
              type: 'warning',
              message: 'Access to developer tools is restricted. Please authenticate first.'
            })
          }
        }
      })

      // Disable right-click for unauthorized users
      document.addEventListener('contextmenu', (e) => {
        if (!this.$store.state.auth.isAuthenticated) {
          e.preventDefault()
        }
      })
    }
  },
  created() {
    // Initialize authentication is already handled in main.js
    // Security: Prevent unauthorized access to developer tools
    this.preventDevTools()
  }
}
</script>

<style>
:root {
  --ds-primary-900: #0a2540;
  --ds-primary-700: #0d3356;
  --ds-primary-500: #1a7fd4;
  --ds-primary-400: #3b9ae8;
  --ds-primary-100: #e8f4fd;
  --ds-accent: #10b981;
  --ds-accent-light: #d1fae5;
  --ds-neutral-900: #111827;
  --ds-neutral-700: #374151;
  --ds-neutral-500: #6b7280;
  --ds-neutral-300: #d1d5db;
  --ds-neutral-200: #e5e7eb;
  --ds-neutral-100: #f3f4f6;
  --ds-neutral-50: #f9fafb;
  --ds-white: #ffffff;
  --ds-danger: #ef4444;
  --ds-warning: #f59e0b;
  --ds-font-display: 'Plus Jakarta Sans', sans-serif;
  --ds-font-body: 'DM Sans', sans-serif;
  --ds-font-mono: 'JetBrains Mono', monospace;
  --ds-radius-sm: 6px;
  --ds-radius-md: 8px;
  --ds-radius-lg: 12px;
  --ds-radius-xl: 16px;
  --ds-shadow-sm: 0 1px 2px rgba(0,0,0,0.05);
  --ds-shadow-md: 0 4px 12px rgba(0,0,0,0.08);
  --ds-shadow-lg: 0 8px 24px rgba(0,0,0,0.12);
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

#app {
  font-family: var(--ds-font-body);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 100vh;
  position: relative;
}

.main-content {
  position: relative;
  z-index: 1;
  margin-top: 0;
  padding-top: 70px; /* Account for fixed navbar height */
}

/* Global styles */
body {
  margin: 0;
  padding: 0;
  font-family: var(--ds-font-body);
  line-height: 1.6;
  color: var(--ds-neutral-900);
  overflow-x: hidden;
  position: relative;
  padding-top: 0;
}

html {
  margin: 0;
  padding: 0;
  position: relative;
}

/* Transitions */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Utility classes */
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.btn {
  padding: 0.8rem 2rem;
  border: none;
  border-radius: var(--ds-radius-md);
  cursor: pointer;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  font-family: var(--ds-font-body);
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-primary {
  background: var(--ds-primary-500);
  color: var(--ds-white);
  box-shadow: var(--ds-shadow-sm);
}

.btn-primary:hover {
  background: var(--ds-primary-400);
  transform: translateY(-2px);
  box-shadow: var(--ds-shadow-md);
  color: var(--ds-white);
}

.btn-secondary {
  background: transparent;
  color: var(--ds-neutral-700);
  border: 1px solid var(--ds-neutral-300);
}

.btn-secondary:hover {
  background: var(--ds-neutral-100);
  border-color: var(--ds-neutral-300);
  color: var(--ds-neutral-900);
}

.btn-outline {
  background: transparent;
  color: var(--ds-primary-500);
  border: 1px solid var(--ds-primary-500);
}

.btn-outline:hover {
  background: var(--ds-primary-500);
  color: var(--ds-white);
}

/* Animation classes */
.slide-in-left {
  animation: slideInLeft 0.8s ease-out;
}

.slide-in-right {
  animation: slideInRight 0.8s ease-out;
}

.fade-in-up {
  animation: fadeInUp 0.8s ease-out;
}

@keyframes slideInLeft {
  from {
    transform: translateX(-100px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideInRight {
  from {
    transform: translateX(100px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes fadeInUp {
  from {
    transform: translateY(50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Responsive design */
@media (max-width: 768px) {
  .container {
    padding: 0 15px;
  }

  .btn {
    padding: 0.7rem 1.5rem;
    font-size: 0.9rem;
  }
}

/* Scrollbar styling */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: var(--ds-neutral-100);
}

::-webkit-scrollbar-thumb {
  background: var(--ds-neutral-300);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: var(--ds-neutral-500);
}
</style>
